/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { flyoutHeaderBackToSX, headerFlyoutContentSX } from '@/components/content/Header/styles/navBar/dropMenu';
import { headerNavBarItemSX } from '@/components/content/Header/styles/navBar/item';
import { useLocalization } from '@/data/Localization';
import { PageLink } from '@/data/Navigation';
import { combineSX } from '@/utils/combineSX';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Button, MenuItem, MenuList, Slide, Stack, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { headerFlyoutMenuItemLabelSX } from '@/components/content/Header/styles/navBar/dropMenuItem';
import {
	NavbarDialogPaperPropsSX, NavbarDialogSX, NavbarMenuItemSX, NavbarSubMenuContainerSX,
	NavbarMenuListSX,
	NavbarSubMenuItemSX, NavbarSubMenuSX
} from '@/components/content/Header/styles/navBar/navbarDialog';
import { HeaderMobileToggleButton } from '@/components/content/Header/parts/MobileToggleButton';
import { CLOSE, OPEN, SHOW_MENU, SHOW_MOBILE_POPOVER } from '@/data/constants/headerMenu';
import { MobilePopOver } from './MobilePopOver';
import { useNavBarCategoriesMenu } from '@/data/Content/NavBarCategoriesMenu';
import { forwardRef, ReactElement, Ref } from 'react';
import { TransitionProps } from '@mui/material/transitions';

type Props = {
	tree: PageLink[];
	label: string;
};

export const NavBarCategoriesMenu = ({ tree, label }: Props) => {

	const AllCategoriesNL = useLocalization('AllCategoriesExpandedMenu');
	
	const {
		open,
		activeMenu,
		setActiveMenu,
		showMobilePopOver,
		isMobile,
		pathName,
        handleLevelNavigation,
		handleToolTip,
		topMenuListRef,
		subMenuRefs,
		handleMenuKeyDown,
	} = useNavBarCategoriesMenu();

	return (
		<>
			<Button
				variant="text"
				sx={combineSX([headerNavBarItemSX, {'&.MuiButton-text':{px:0}}])}
				onClick={() => handleLevelNavigation(OPEN)}
				id="all_categories_label"
				aria-label={'Categorieën'}
			>
				{label}
			</Button>

			{isMobile ? <HeaderMobileToggleButton toggleDrawer={() => handleToolTip()} open={open} /> : null}

			<Dialog
				open={open}
				fullScreen
				onClose={() => handleLevelNavigation(CLOSE)}
				sx={NavbarDialogSX}
				PaperProps={{ sx: NavbarDialogPaperPropsSX(activeMenu) }}
				TransitionComponent={Transition}
			>
				{showMobilePopOver ? (
					<MobilePopOver handleLevelNavigation={handleLevelNavigation} />
				) : (
					<Stack className="menu-navigation-elem" overflow="hidden">
						<MenuList
							id="all_categories_menu"
							role="menubar"
							aria-labelledby="all_categories_label"
							onKeyDown={handleMenuKeyDown}
							sx={NavbarMenuListSX(activeMenu)}
							ref={(el) => (topMenuListRef.current = el)}
						>
							<MenuItem 
								role="none" 
								tabIndex={-1} 
								id="all_categories_menu_back_menuitem"
								sx={{width:400, bgcolor:'#fff'}}
							>
								<Button
									sx={flyoutHeaderBackToSX}
									onClick={() => handleLevelNavigation(isMobile ? SHOW_MOBILE_POPOVER : CLOSE)}
									variant="textButton"
									tabIndex={0}
									id="all_categories_menu_back_button"
									role='menuitem'
								>
									<ArrowBackIosIcon />
									<Typography m={0}>{AllCategoriesNL.Actions.BackTo.t()}</Typography>
								</Button>
							</MenuItem>
							<MenuItem 
								id="all_categories_label_menuitem"
								sx={{ my: 0.5 }} 
								tabIndex={-1}
								role="none"
							>
								<Typography fontWeight="bold">{label}</Typography>
							</MenuItem>

							{tree?.map((tr, index) => (
								<MenuItem
									key={index}
									id={`menuitem-${tr.label}`}
									role="none"
									sx={NavbarMenuItemSX}
								>
									<Button 
										variant='textButton'
										fullWidth
										role="menuitem"
										tabIndex={0}
										aria-haspopup={!!tr.children?.length} 
										aria-expanded={index === activeMenu}
										onClick={() => setActiveMenu(index)}
										// IMPORTANT: data-index so we can map submenu -> parent reliably
										data-index={String(index)}
										className={index === activeMenu ? 'selected' : ''}
										id={`header-link-level1-${tr.url?.replace('/', '')}`}
									>
										<Typography component="span" sx={headerFlyoutMenuItemLabelSX}>
											{tr.label}
										</Typography>
										<ArrowForwardIosIcon />
									</Button>
								

									{index === activeMenu ? (
										<Stack sx={NavbarSubMenuContainerSX} id={`navbar_submenu_container_${tr.identifier}`}>
											<MenuList
												role="menu"
												sx={NavbarSubMenuSX}
												ref={(el) => (subMenuRefs.current[index] = el)}
												onKeyDown={handleMenuKeyDown}
												data-submenu-index={String(index)}
											>
												{isMobile ? (
													<Stack height="3em" px={2} mt={2} mb={1}>
														<Button
															id="mobile_submenu_close_button"
															sx={flyoutHeaderBackToSX}
															onClick={(event) => {
																event.stopPropagation();
																handleLevelNavigation(SHOW_MENU);
															}}
															onMouseDown={(e) => e.stopPropagation()}
															variant="textButton"
														>
															<ArrowBackIosIcon />
															<Typography m={0}>{AllCategoriesNL.Actions.BackToOverview.t()}</Typography>
														</Button>
													</Stack>
												) : null}

												<MenuItem role="none" sx={NavbarSubMenuItemSX} tabIndex={-1}>
													<Linkable role="menuitem" tabIndex={0} href={tr.url} sx={{ fontWeight: 'bold' }}>
														{tr.label}
													</Linkable>
												</MenuItem>
												<Box
													onClick={() => handleLevelNavigation(CLOSE)}
													className="header-flyout_Sub_Category_Top_Text_eSpot_rv"
													sx={combineSX([headerFlyoutContentSX])}
													id={`${tr.identifier}_Sub_Category_Top_Text_eSpot_rv`}
												>
													<ContentRecommendation
														id={`${tr.identifier}_Sub_Category_Top_Text_eSpot_rv`}
														properties={{ emsName: `${tr.identifier}_Sub_Category_Top_Text_eSpot_rv` }}
													/>
												</Box>

												{tr.children?.map((tr1, index1) => (
													<MenuItem
														tabIndex={-1}
														key={index1}
														role="none"
														sx={combineSX([NavbarSubMenuItemSX])}
														onClick={() => (pathName === tr1.url ? handleLevelNavigation(CLOSE) : null)}
														id={`sub-menuitem-${tr1.label}`}
													>
														<Linkable href={tr1.url} tabIndex={0} role="menuitem" id={`header-link-level2-${tr1.url?.replace('/', '')}`}>
															<>
																<Typography sx={headerFlyoutMenuItemLabelSX}>{tr1.label}</Typography>
																<ArrowForwardIosIcon sx={{ fontSize: 12 }} />
															</>
														</Linkable>
													</MenuItem>
												))}
												<Box
													onClick={() => handleLevelNavigation(CLOSE)}
													className="header-flyout_Category_Brand_List_eSpot"
													sx={headerFlyoutContentSX}
													id={`${tr.identifier}_Category_Brand_List_eSpot`}
												>
													<ContentRecommendation
														id={`${tr.identifier}_Category_Brand_List_eSpot`}
														properties={{ emsName: `${tr.identifier}_Category_Brand_List_eSpot` }}
													/>
												</Box>
												<Box
													onClick={() => handleLevelNavigation(CLOSE)}
													className="header-flyout_Category_Site_Wide_Promotional_Bar_espot_rv"
													sx={headerFlyoutContentSX} id={`${tr.identifier}_Category_Site_Wide_Promotional_Bar_espot_rv`}
												>
													<ContentRecommendation
														id={`${tr.identifier}_Category_Site_Wide_Promotional_Bar_espot_rv`}
														properties={{ emsName: `${tr.identifier}_Category_Site_Wide_Promotional_Bar_espot_rv` }}
													/>
												</Box>
											</MenuList>
										</Stack>
									) : null}
								</MenuItem>
							))}
						</MenuList>
					</Stack>
				)}
			</Dialog>
		</>
	);
};


const Transition = forwardRef(function Transition(
	props: TransitionProps & {
		children: ReactElement<any, any>;
	},
	ref: Ref<unknown>,
) {
	return <Slide direction="right" ref={ref} {...props} />;
});